/**
 * XRLinkDevicePage — v10.8.0
 * Genera un QR temporal que un dispositivo XR (teléfono/tablet/gafas) puede
 * escanear para vincularse a una sesión del usuario actual.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { QrCode, ArrowClockwise, Copy, DeviceMobile, CheckCircle } from '@phosphor-icons/react';

export default function XRLinkDevicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [linkData, setLinkData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [deviceHint, setDeviceHint] = useState('');
  const [sessionId, setSessionId] = useState(searchParams.get('session') || '');

  const createLink = async () => {
    try {
      const { data } = await api.post('/xr/link/create', {
        session_id: sessionId || null,
        device_hint: deviceHint || null,
        ttl_seconds: 300,
      });
      setLinkData(data);
      const secs = Math.max(0, Math.floor((new Date(data.expires_at).getTime() - Date.now()) / 1000));
      setTimeLeft(secs);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar QR');
    }
  };

  useEffect(() => {
    if (!linkData || timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [linkData, timeLeft]);

  const fullUrl = linkData ? `${window.location.origin}${linkData.qr_url}` : '';

  const copy = async () => {
    try { await navigator.clipboard.writeText(fullUrl); toast.success('URL copiada'); }
    catch { toast.error('No se pudo copiar'); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="xr-link-page">
      <div>
        <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 mb-2">SimuClinic XR</Badge>
        <h1 className="text-2xl font-bold text-slate-900">Vincular dispositivo XR</h1>
        <p className="text-sm text-slate-500 mt-1">
          Genera un código QR temporal (single-use, expira en 5 min). El dispositivo lo escanea y queda vinculado a tu sesión.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><QrCode size={20} /> Configuración del enlace</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nombre del dispositivo (opcional)</Label>
              <Input value={deviceHint} onChange={(e) => setDeviceHint(e.target.value)} placeholder="iPhone 15 Pro / iPad / Meta Quest 3" data-testid="device-hint" />
            </div>
            <div>
              <Label className="text-xs">ID de sesión activa (opcional)</Label>
              <Input value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="uuid de simulation_session" />
            </div>
          </div>
          <Button onClick={createLink} className="gap-2" data-testid="create-link-btn">
            {linkData ? <><ArrowClockwise size={14} /> Regenerar QR</> : <><QrCode size={14} /> Generar QR</>}
          </Button>
        </CardContent>
      </Card>

      {linkData && (
        <Card className="border-cyan-200">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="p-4 bg-white border-2 border-cyan-200 rounded-lg" data-testid="qr-canvas">
                <QRCodeSVG value={fullUrl} size={220} level="M" includeMargin={false} />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-500 tracking-widest">URL de vinculación</div>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-xs bg-slate-50 px-2 py-1.5 rounded border border-slate-200 truncate">{fullUrl}</code>
                    <Button size="icon" variant="ghost" onClick={copy}><Copy size={14} /></Button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-500 tracking-widest">Expira en</div>
                  <div className={`text-3xl font-mono font-bold ${timeLeft < 30 ? 'text-red-600' : 'text-cyan-600'}`}>
                    {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
                  </div>
                </div>
                <div className="flex items-start gap-2 text-xs text-slate-600">
                  <DeviceMobile size={14} className="mt-0.5 text-slate-400 shrink-0" />
                  <span>Abre la cámara del dispositivo y apunta al QR. Se abrirá el player XR autenticado a tu sesión.</span>
                </div>
              </div>
            </div>
            {timeLeft === 0 && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                Token expirado. Genera uno nuevo.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Button variant="ghost" onClick={() => navigate(-1)}>Volver</Button>
    </div>
  );
}
